<script lang="ts" setup>
import Top from '~/components/Top/Top.vue'
import BusinessModel from '~/domains/esg/components/BusinessModel/BusinessModelV2.vue'
import SignificanceMatrix from '~/domains/esg/strategy/components/SignificanceMatrix/SignificanceMatrix.vue'
import type { IProps } from '@/domains/career/components/career-teamWithHeader/career-teamWithHeader.types'
import { useSustainableDevelopment } from '@/domains/esg/composables/content/useSustainableDevelopment'

const { chainOfValues } = useSustainableDevelopment()
const { isMobile } = useBSBreakpoints()
const { t, locale } = useI18n()

defineProps<IProps>()

useHead({
  title: t('pageTitles.sustainableDevelopment.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.ourApproach.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.sustainableDevelopment.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.sustainableDevelopment.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.sustainableDevelopment.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.sustainableDevelopment.canonical.pl'),
    },
  ],
})

</script>

<template>
  <div>
    <Top
      :header="$t('sustainableDevelopment.top.header')"
      :subheader="$t('sustainableDevelopment.top.subheader')"
      :text="$t('sustainableDevelopment.top.text')"
    />

    <TextWithImage
      id="summary"
      class="section1"
      :header="$t('sustainableDevelopment.summary.header')"
      :text="$t('sustainableDevelopment.summary.text')"
      image="/images/sustainable-development/benefitsystems-responsible-business-sustainable-development-summary.webp"
      :button="{
        text: $t('sustainableDevelopment.summary.button.text'),
        to: $t('sustainableDevelopment.summary.button.link'),
        icon: '/icons/icon-download.svg',
        variant: 'primary',
        target: '_blank'
      }"
    />

    <BusinessModel
      :header="$t('responsibleBusiness.businessModel.header')"
      :subheader="$t('responsibleBusiness.businessModel.subheader')"
      :link="{
        to: $t('sustainableDevelopment.businessModel.link.url'),
        text: $t('sustainableDevelopment.businessModel.link.text'),
        external: true
      }"
    />

    <UiSection
      id="key-stakeholders"
      class="key-stakeholders"
      color="gray"
    >
      <UiHeader
        position="center"
        :header="$t('sustainableDevelopment.keyStakeholders.header')"
        :subheader="$t('sustainableDevelopment.keyStakeholders.subheader')"
      />

      <div class="section__container">
        <div class="section__row">
          <div class="section__col section__col--img">
            <img
              :alt="$t('sustainableDevelopment.keyStakeholders.header')"
              :src="$t('sustainableDevelopment.keyStakeholders.img.src')"
            >

            <NuxtLink
              class="sbm__link"
              :external="true"
              target="_blank"
              :to="$t('sustainableDevelopment.keyStakeholders.link.url')"
            >
              {{ $t('sustainableDevelopment.keyStakeholders.link.text') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </UiSection>

    <SignificanceMatrix />

    <UiSection
      id="report-archive"
      class="reports-archive"
      color="psychology-100"
    >
      <div class="section__container">
        <div class="section__row">
          <div class="section__col">
            <UiHeader :header="$t('sustainableDevelopment.reportArchive.header')" />

            <picture
              v-if="isMobile"
              class="section__picture"
            >
              <source
                media="(width < 1200px)"
                srcset="/images/sustainable-development/benefitsystems-responsible-business-sustainable-reports-archive_m.webp"
              >

              <source
                media="(width >= 1200px)"
                srcset="/images/sustainable-development/benefitsystems-responsible-business-sustainable-reports-archive.webp"
              >

              <img
                src="/images/sustainable-development/benefitsystems-responsible-business-sustainable-reports-archive.webp"
                :alt="$t('sustainableDevelopment.reportArchive.header')"
              >
            </picture>

            <p class="reports-archive__text">
              {{ $t('sustainableDevelopment.reportArchive.text') }}
            </p>

            <UiButton
              class="section__button"
              :to="$t('sustainableDevelopment.reportArchive.link.url')"
              :variant="'primary'"
            >
              {{ $t('sustainableDevelopment.reportArchive.link.text') }}
            </UiButton>
          </div>

          <div
            v-if="!isMobile"
            class="section__col section__col--img"
          >
            <picture class="section__picture">
              <source
                media="(width < 1200px)"
                srcset="/images/sustainable-development/benefitsystems-responsible-business-sustainable-reports-archive_m.webp"
              >

              <source
                media="(width >= 1200px)"
                srcset="/images/sustainable-development/benefitsystems-responsible-business-sustainable-reports-archive.webp"
              >

              <img
                src="/images/sustainable-development/benefitsystems-responsible-business-sustainable-reports-archive.webp"
                :alt="$t('sustainableDevelopment.reportArchive.header')"
              >
            </picture>
          </div>
        </div>
      </div>
    </UiSection>
  </div>
</template>

<style lang="scss" scoped>
:deep(.top) {
  min-height: 55.4rem;
  margin-bottom: 1.6rem;
}

:deep(.top__header) {
  overflow-wrap: break-word;
  @include h7;
}

:deep(.section) {
  h3 {
    margin-bottom: 1.2rem;
    @include h10;
  }

  .matrix-legend__heading {
    @include body-1-medium;
    font-weight: 700;
    line-height: 3.2rem;
  }
}

.section {
  &.section1 {
    margin-bottom: 0;
    padding-bottom: 0;

    :deep(.text-with-image__actions .button) {
      align-self: flex-start;
    }
  }

  &.key-stakeholders {
    margin-bottom: 0;

    .section__col--img {
      padding-top: 4.8rem;
      text-align: center;
      
      img {
        max-width: 77.7rem;
        width: 100%;
      }
    }

    :deep(.ui-header__subheader) {
      br {
        display: none;
      }
    }

    .sbm__link {
      margin: 3.2rem auto 0;
    }
  }

  &.reports-archive {
    &.section--psychology-100 {
      background-color: $psychology-100;
    }

    :deep(.ui-header) {
      margin-bottom: 3.2rem;
    }

    .section__button {
      margin-top: 3.2rem;
    }

    .reports-archive__text {
      @include h10;
      margin-bottom: 5.6rem;
    }

    .section__col--img {
      text-align: center;
    }

    .section__picture {
      img {
        margin-bottom: 3.2rem;
        border-radius: 1.6rem;
      }
    }
  }

  &.significant-matrix {
    margin-bottom: 0;
  }
}

.sbm__link {
  @include body-1;
  display: block;
  margin: 3.2rem auto 0;
  text-align: center;
  text-decoration: underline;
}

@include atXS {
  :deep(.top__header) {
    @include h5;
  }
}

@include atS {
  :deep(.top__header) {
    @include h4;
  }
}

@include atM {

  :deep(.top) {
    .top__item {
      max-width: 65%;
    }
  }

  :deep(.top__header) {
    word-break: normal;
    @include h3;
  }

  :deep(.ui-header) {
    margin-bottom: 5.6rem;
  }

  :deep(.section) {
    @include h8;
    margin-bottom: 6.4rem;
  }

  .section {
    &.section1 {
      margin-bottom: 0;

      :deep(.ui-header) {
        margin-bottom: 5.6rem;
      }
    }

    &.key-stakeholders {
      :deep(.ui-header__subheader) {
        br {
          display: inline;
        }
      }
    }

    &.reports-archive {
      .section__button {
        margin-top: 3.2rem;
      }

      .reports-archive__text {
        @include h9;
      }
    }
  }

  .sbm__link {
    @include body-1;
    display: block;
    margin: 9.6rem auto 0;
    text-align: center;
    text-decoration: underline;
  }
}

@include atXL {
  :deep(.top__header) {
    @include h1;
  }
}
</style>
